import axios from 'axios'
import hash from 'object-hash'

import { CommonChartConfig } from '../'
import { mergeDeepAll } from '../utils'
import {
  CompassAPIDataBase,
  CompassAPIMultiSeriesChartData,
  getCompassAPIChartData,
  getDataSources,
  getMultipleColumnChartProps,
  getMultipleLineChartProps,
  magnitudePointFormatter,
} from '../utils/compass'
import { datetimeXAxis } from '../utils/constants'
import { Palette } from '../utils/constants'
import type { BaseContext } from './'

const INSIDER_TRADING_CHARTS_PATH = '/api/charts/insider-trading'

export namespace InsiderTrading {
  export namespace PosicaoConsolidada {
    const CONTEXT_ID = 'equity:insider_trading:posicao_consolidada'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    interface ChartOptions {
      ticker: string
      segmentation: string[]
      operation: string
    }

    export interface ChartConfig {
      title?: string
      options: ChartOptions
      context: typeof CONTEXT_ID
    }

    interface ChartData extends CompassAPIDataBase {
      title: string
      data: {
        name: string
        title: string
        data: { date: string; value: number }[]
      }[]
    }

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: INSIDER_TRADING_CHARTS_PATH + '/insider-graph',
        method: 'POST',
        data: config.options,
      }).then((response) => response.data)

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleColumnChartProps(
        Context,
        {
          plotOptions: { column: { stacking: 'normal' } },
          xAxis: datetimeXAxis[0],
          yAxis: [
            {
              id: 'default',
              opposite: false,
              title: { text: 'Volume' },
            },
            {
              id: 'cot_media',
              opposite: true,
              title: { text: 'Cotação média' },
            },
          ],
          tooltip: {
            pointFormatter: magnitudePointFormatter(),
            xDateFormat: '%m/%Y',
            shared: true,
            split: false,
          },
        },
        {
          type: data.data.map((s) =>
            ['Cotação Média', 'venda', 'compra'].includes(s.title)
              ? 'line'
              : 'column'
          ),
          yAxis: data.data.map((s) =>
            s.title === 'Cotação Média' ? 'cot_media' : 'default'
          ),
          name: data.data.map((s) => s.title),
        }
      )(config, data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${hash(config, { unorderedArrays: true })}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace PosicaoConsolidadaTopBottom {
    const CONTEXT_ID = 'equity:insider_trading:posicao_consolidada_top_bottom'

    export const __typelist: {
      config: ChartConfig
      data: TopBottomData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      TopBottomData
    >

    interface ChartOptions {
      month: string
    }

    export interface ChartConfig {
      title?: string
      options: ChartOptions
      context: typeof CONTEXT_ID
    }

    interface TopBottomPoint {
      company: string
      segmentation: string
      value: number
    }

    interface TopBottomData extends CompassAPIDataBase {
      data: { bottom: TopBottomPoint[]; top: TopBottomPoint[] }
    }

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: INSIDER_TRADING_CHARTS_PATH + '/posicao-consolidada/top-bottom',
        method: 'POST',
        data: { month: config.options.month },
      }).then((response) => response.data)

    const getChartProps = (config: ChartConfig, data: TopBottomData) => {
      const series = [
        {
          data: data.data
            ? [
                ...data.data.top,
                { company: '', value: 0, segmentation: '' },
                ...data.data.bottom,
              ].map((column, idx) => ({
                name: `${column.company} ${
                  column.segmentation ? `(${column.segmentation})` : ''
                }`,
                y: column.value / 1000000,
                color:
                  column.value >= 0
                    ? '#30ba09' // success.main
                    : '#f4643d', // error.light
                ...(idx === data.data.top.length
                  ? { dataLabels: { enabled: false } }
                  : {}),
              }))
            : [],
          dataLabels: { enabled: true, format: '{point.y:.2f} mi' },
          color: Palette.green,
          showInLegend: false,
          yAxis: 'primary',
        },
      ]
      const creditsConfig = getDataSources(data.data_sources)

      const tooltip = {
        shared: true,
        split: undefined,
        valueDecimals: 2,
        valueSuffix: 'mi',
      }

      const xAxis = [
        {
          plotLines: [
            data
              ? {
                  dashStyle: 'LongDash' as const,
                  value: data.data.top.length,
                  label: {
                    formatter: () => {
                      const top =
                        data && data.data.top.length > 0
                          ? `Top ${data.data.top.length}`
                          : ''
                      const bottom =
                        data && data.data.bottom.length > 0
                          ? `Bottom ${data.data.bottom.length}`
                          : ''
                      return `${top}<br />${bottom}`
                    },
                  },
                  zIndex: 10,
                }
              : {},
          ],
          type: 'category' as const,
        },
      ]

      const yAxis = [
        {
          id: 'primary',
          title: { text: '' },
          labels: { format: '{value} mi' },
        },
      ]

      return {
        id: Context.getChartId(config),
        title: config.title,
        options: mergeDeepAll([
          creditsConfig,
          {
            chart: { type: 'bar' },
            tooltip,
            series,
            stockTools: { gui: { enabled: false } },
            xAxis,
            yAxis,
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${hash(config, { unorderedArrays: true })}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace PosicaoConsolidadaIbov {
    const CONTEXT_ID = 'equity:insider_trading:posicao_consolidada_ibov'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'ibov'
      context: typeof CONTEXT_ID
      title: string
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) =>
        getMultipleLineChartProps(
          Context,
          {
            addMinMaxIndicators: true,
            tooltip: {
              pointFormatter: magnitudePointFormatter(),
              xDateFormat: '%B %Y',
            },
          },
          { name: [''] }
        )(config, data),
    }
  }
}
